import { create } from 'zustand';
import { coreApi, coreApiAs } from '@/integrations/api/active';
import { supabase } from '@/integrations/supabase/client';
import type { AppNotification, EntityFollower, CreateNotificationArgs } from '@/modules/notifications/types';
import { useNotificationPrefsStore } from '@/modules/notifications/notification-prefs-store';
import { useWorkspaceStore } from '@/modules/workspaces/workspace-store';
import type { StoredSession } from '@/modules/auth/session-store';
import { getSessionClient, useSessionStore } from '@/modules/auth/session-store';
import { workspaceHasFeature } from '@/modules/workspaces/workspace-features';

/** Returns the Supabase client authenticated as the given user, or the shared
 *  client if userId is the active session (or unknown). */
function clientForUser(userId?: string) {
  if (!userId) return supabase;
  const { sessions, activeUserId } = useSessionStore.getState();
  if (userId === activeUserId) return supabase;
  const sess = sessions.find((s) => s.user_id === userId && !s.stale);
  return sess ? (getSessionClient(sess) as any) : supabase;
}

function tokenForUser(userId?: string): string | null {
  if (!userId) return null;
  const { sessions, activeUserId } = useSessionStore.getState();
  if (userId === activeUserId) return null;
  return sessions.find((s) => s.user_id === userId && !s.stale)?.access_token ?? null;
}

let _notifChannel: ReturnType<typeof supabase.channel> | null = null;
let _followersChannel: ReturnType<typeof supabase.channel> | null = null;
let _notificationsRequestGeneration = 0;
let _followersRequestGeneration = 0;
let _followersWorkspaceId: string | null = null;
let _sessionSubscriptionGeneration = 0;
let _notificationSubscriptionGeneration = 0;
let _followersSubscriptionGeneration = 0;

// Per-session Realtime channels for notifications (keyed by user_id)
interface SessionSub { client: any; channel: any; }
const _sessionSubs = new Map<string, SessionSub>();

function rowToNotification(r: any): AppNotification {
  return {
    id: r.id,
    workspaceId: r.workspace_id,
    recipientUserId: r.recipient_user_id,
    recipientMemberId: r.recipient_member_id ?? undefined,
    actorUserId: r.actor_user_id ?? undefined,
    actorName: r.actor_name ?? undefined,
    type: r.type,
    entityType: r.entity_type ?? undefined,
    entityId: r.entity_id ?? undefined,
    entityLabel: r.entity_label ?? undefined,
    body: r.body ?? '',
    linkData: r.link_data ?? undefined,
    readAt: r.read_at ?? undefined,
    createdAt: r.created_at,
  };
}

function rowToFollower(r: any): EntityFollower {
  return {
    id: r.id,
    workspaceId: r.workspace_id,
    entityType: r.entity_type as 'rock' | 'metric',
    entityId: r.entity_id,
    userId: r.user_id,
    memberId: r.member_id,
  };
}

interface NotificationsState {
  notifications: AppNotification[];
  followers: EntityFollower[];
  loading: boolean;
  currentUserId: string | null;

  // Legacy: fetches notifications + followers for one workspace
  fetchAll: (workspaceId: string) => Promise<void>;
  // Global: fetches notifications for current user across all workspaces
  fetchNotificationsGlobal: () => Promise<void>;
  // Per-workspace followers only (used by WorkspaceRoute for comment routing)
  fetchFollowers: (workspaceId: string) => Promise<void>;

  /** False when nothing was written, so a caller can offer a lower-priority alternative. */
  createNotification: (args: CreateNotificationArgs) => boolean;
  markRead: (id: string) => Promise<void>;
  markUnread: (id: string) => Promise<void>;
  // workspaceId optional — omit to mark all workspaces
  markAllRead: (workspaceId?: string) => Promise<void>;
  follow: (workspaceId: string, entityType: 'rock' | 'metric', entityId: string) => Promise<void>;
  unfollow: (workspaceId: string, entityType: 'rock' | 'metric', entityId: string) => Promise<void>;
  getFollowersOf: (entityType: 'rock' | 'metric', entityId: string) => EntityFollower[];
  deleteNotification: (id: string) => Promise<void>;
  // workspaceId optional — omit to delete all read across all workspaces
  deleteAllRead: (workspaceId?: string) => Promise<void>;

  // Global notifications subscription (no workspace filter) — owned by NotificationBell
  subscribeToNotificationsGlobal: () => Promise<void>;
  unsubscribeFromNotificationsGlobal: () => void;

  // Multi-session: one Realtime channel + one fetch per stored session
  subscribeToAllSessions: (sessions: StoredSession[]) => Promise<void>;
  unsubscribeFromAllSessions: () => void;
  fetchNotificationsForAllSessions: (sessions: StoredSession[]) => Promise<void>;

  // Per-workspace followers subscription — owned by WorkspaceRoute
  subscribeToFollowers: (workspaceId: string) => Promise<void>;
  unsubscribeFromFollowers: () => void;

  // Legacy combined subscription (kept for backward compat)
  subscribeToNotifications: (workspaceId: string) => Promise<void>;
  unsubscribeFromNotifications: () => void;
  /** Drop everything on an account change (#142). */
  reset: () => void;
}

/**
 * Notification-owned server cache and realtime coordinator, keyed by user and
 * workspace.
 */
export const useNotificationsStore = create<NotificationsState>((set, get) => ({
  notifications: [],
  followers: [],
  loading: false,
  currentUserId: null,

  // Cleared on an account change (#142) — see AuthContext. These are addressed to
  // a person by definition, so carrying them across a user change shows one
  // account another's mail. `currentUserId` goes too: it is the thing that would
  // otherwise make the next fetch believe it had already run.
  reset: () => {
    _notificationsRequestGeneration += 1;
    _followersRequestGeneration += 1;
    _sessionSubscriptionGeneration += 1;
    _notificationSubscriptionGeneration += 1;
    _followersSubscriptionGeneration += 1;
    _followersWorkspaceId = null;
    set({ notifications: [], followers: [], loading: false, currentUserId: null });
  },

  fetchAll: async (workspaceId) => {
    const notificationGeneration = ++_notificationsRequestGeneration;
    const followerGeneration = ++_followersRequestGeneration;
    set({ loading: true });
    const { data: user } = await supabase.auth.getUser();
    if (
      notificationGeneration !== _notificationsRequestGeneration
      || followerGeneration !== _followersRequestGeneration
    ) return;
    if (!user.user) { set({ loading: false }); return; }

    const [notifResult, followResult] = await Promise.all([
      supabase
        .from('notifications')
        .select('*')
        .eq('workspace_id', workspaceId)
        .eq('recipient_user_id', user.user.id)
        .order('created_at', { ascending: false })
        .limit(200),
      supabase
        .from('entity_followers')
        .select('*')
        .eq('workspace_id', workspaceId),
    ]);

    if (
      notificationGeneration !== _notificationsRequestGeneration
      || followerGeneration !== _followersRequestGeneration
    ) return;

    _followersWorkspaceId = workspaceId;

    set({
      notifications: (notifResult.data ?? []).map(rowToNotification),
      followers: (followResult.data ?? []).map(rowToFollower),
      currentUserId: user.user.id,
      loading: false,
    });
  },

  fetchNotificationsGlobal: async () => {
    const requestGeneration = ++_notificationsRequestGeneration;
    set({ loading: true });
    const { data: user } = await supabase.auth.getUser();
    if (requestGeneration !== _notificationsRequestGeneration) return;
    if (!user.user) { set({ loading: false }); return; }
    const { data } = await supabase
      .from('notifications')
      .select('*')
      .eq('recipient_user_id', user.user.id)
      .order('created_at', { ascending: false })
      .limit(300);
    if (requestGeneration !== _notificationsRequestGeneration) return;
    set({
      notifications: (data ?? []).map(rowToNotification),
      currentUserId: user.user.id,
      loading: false,
    });
  },

  fetchFollowers: async (workspaceId) => {
    const requestGeneration = ++_followersRequestGeneration;
    if (_followersWorkspaceId !== workspaceId) set({ followers: [] });
    const { data } = await supabase
      .from('entity_followers')
      .select('*')
      .eq('workspace_id', workspaceId);
    if (requestGeneration !== _followersRequestGeneration) return;
    _followersWorkspaceId = workspaceId;
    set({ followers: (data ?? []).map(rowToFollower) });
  },

  createNotification: (args) => {
    const ws = useWorkspaceStore.getState().workspaces.find((w) => w.id === args.workspaceId);
    // Solo workspaces have no one else to notify; a future free cohort could
    // allow more than one member, so use the actual purchased capacity.
    if (ws && (ws.purchasedSeats ?? 1) <= 1) return false;
    if (!useNotificationPrefsStore.getState().isRecipientEnabled(args.recipientUserId, args.type, args.entityType)) {
      return false;
    }
    // The acting identity's client, not the active one: a write against another
    // signed-in identity's workspace has to insert under their RLS, not ours.
    clientForUser(args.actorUserId).from('notifications').insert({
      workspace_id: args.workspaceId,
      recipient_user_id: args.recipientUserId,
      recipient_member_id: null,
      actor_user_id: args.actorUserId ?? null,
      actor_name: args.actorName ?? null,
      type: args.type,
      entity_type: args.entityType ?? null,
      entity_id: args.entityId ?? null,
      entity_label: args.entityLabel ?? null,
      body: args.body,
      link_data: args.linkData ?? null,
    } as any).then(({ error }: { error: any }) => {
      if (error) console.error('[notifications] insert failed', error);
    });
    return true;
  },

  markRead: async (id) => {
    const now = new Date().toISOString();
    const n = get().notifications.find((x) => x.id === id);
    set((s) => ({ notifications: s.notifications.map((x) => x.id === id ? { ...x, readAt: now } : x) }));
    await coreApiAs(tokenForUser(n?.recipientUserId), '/notifications/read', { ids: [id], read: true });
  },

  markUnread: async (id) => {
    const n = get().notifications.find((x) => x.id === id);
    set((s) => ({ notifications: s.notifications.map((x) => x.id === id ? { ...x, readAt: undefined } : x) }));
    await coreApiAs(tokenForUser(n?.recipientUserId), '/notifications/read', { ids: [id], read: false });
  },

  markAllRead: async (workspaceId?) => {
    const now = new Date().toISOString();
    const { sessions } = useSessionStore.getState();
    set((s) => ({
      notifications: s.notifications.map((n) =>
        (!workspaceId || n.workspaceId === workspaceId) && !n.readAt ? { ...n, readAt: now } : n
      ),
    }));
    await Promise.all(
      sessions.filter((s) => !s.stale).map((sess) =>
        coreApiAs(tokenForUser(sess.user_id), '/notifications/read', {
          read: true,
          ...(workspaceId ? { workspace_id: workspaceId } : {}),
        })),
    );
  },

  follow: async (workspaceId, entityType, entityId) => {
    const { data: authData } = await supabase.auth.getUser();
    if (!authData.user) return;
    const follower: EntityFollower = {
      id: crypto.randomUUID(),
      workspaceId,
      entityType,
      entityId,
      userId: authData.user.id,
      memberId: authData.user.id,
    };
    set((s) => ({ followers: [...s.followers, follower] }));
    const { data } = await coreApi('/followers', {
      workspace_id: workspaceId,
      entity_type: entityType,
      entity_id: entityId,
    });
    const stored = data?.[0];
    if (stored) {
      set((s) => ({
        followers: s.followers.map((f) =>
          f.id === follower.id ? rowToFollower(stored) : f
        ),
      }));
    }
  },

  unfollow: async (workspaceId, entityType, entityId) => {
    const { data: authData } = await supabase.auth.getUser();
    if (!authData.user) return;
    set((s) => ({
      followers: s.followers.filter(
        (f) =>
          !(f.entityType === entityType &&
            f.entityId === entityId &&
            f.userId === authData.user!.id)
      ),
    }));
    await coreApi('/followers', {
      workspace_id: workspaceId,
      entity_type: entityType,
      entity_id: entityId,
    }, 'DELETE');
  },

  getFollowersOf: (entityType, entityId) => {
    return get().followers.filter(
      (f) => f.entityType === entityType && f.entityId === entityId
    );
  },

  deleteNotification: async (id) => {
    const n = get().notifications.find((x) => x.id === id);
    set((s) => ({ notifications: s.notifications.filter((x) => x.id !== id) }));
    await coreApiAs(tokenForUser(n?.recipientUserId), '/notifications', { ids: [id] }, 'DELETE');
  },

  deleteAllRead: async (workspaceId?) => {
    const { sessions } = useSessionStore.getState();
    const toDelete = get().notifications
      .filter((n) => (!workspaceId || n.workspaceId === workspaceId) && !!n.readAt);
    if (!toDelete.length) return;
    const ids = toDelete.map((n) => n.id);
    set((s) => ({ notifications: s.notifications.filter((n) => !ids.includes(n.id)) }));
    await Promise.all(
      sessions.filter((s) => !s.stale).map((sess) =>
        coreApiAs(tokenForUser(sess.user_id), '/notifications', workspaceId ? { workspace_id: workspaceId } : {}, 'DELETE')),
    );
  },

  subscribeToNotificationsGlobal: async () => {
    const subscriptionGeneration = ++_notificationSubscriptionGeneration;
    if (_notifChannel) { supabase.removeChannel(_notifChannel); _notifChannel = null; }
    const { data: authData } = await supabase.auth.getUser();
    if (subscriptionGeneration !== _notificationSubscriptionGeneration) return;
    if (!authData.user) return;
    const userId = authData.user.id;

    const setup = () => {
      _notifChannel = supabase
        .channel(`notifications-global-${userId}-${Date.now()}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'notifications',
            filter: `recipient_user_id=eq.${userId}`,
          },
          (payload) => {
            if (subscriptionGeneration !== _notificationSubscriptionGeneration) return;
            const n = rowToNotification(payload.new);
            set((s) => {
              if (s.notifications.some((x) => x.id === n.id)) return s;
              return { notifications: [n, ...s.notifications] };
            });
          }
        )
        .subscribe((status, err) => {
          if (subscriptionGeneration !== _notificationSubscriptionGeneration) return;
          if (status === 'CHANNEL_ERROR') {
            console.error('[notifications] realtime channel error — will retry', err);
            supabase.removeChannel(_notifChannel!);
            _notifChannel = null;
            setTimeout(setup, 3000);
          } else if (status === 'TIMED_OUT') {
            console.warn('[notifications] realtime channel timed out — will retry');
            supabase.removeChannel(_notifChannel!);
            _notifChannel = null;
            setTimeout(setup, 3000);
          }
        });
    };

    setup();
  },

  unsubscribeFromNotificationsGlobal: () => {
    _notificationSubscriptionGeneration += 1;
    if (_notifChannel) {
      supabase.removeChannel(_notifChannel);
      _notifChannel = null;
    }
  },

  subscribeToFollowers: async (workspaceId) => {
    get().unsubscribeFromFollowers();
    const activeGeneration = _followersSubscriptionGeneration;

    const setupFollowers = () => {
      _followersChannel = supabase
        .channel(`entity-followers-${workspaceId}-${Date.now()}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'entity_followers',
            filter: `workspace_id=eq.${workspaceId}`,
          },
          (payload) => {
            if (activeGeneration !== _followersSubscriptionGeneration) return;
            const f = rowToFollower(payload.new);
            set((s) => {
              if (s.followers.some((x) => x.id === f.id)) return s;
              const withoutTemp = s.followers.filter(
                (x) => !(x.entityType === f.entityType && x.entityId === f.entityId && x.userId === f.userId)
              );
              return { followers: [...withoutTemp, f] };
            });
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'DELETE',
            schema: 'public',
            table: 'entity_followers',
            filter: `workspace_id=eq.${workspaceId}`,
          },
          (payload) => {
            if (activeGeneration !== _followersSubscriptionGeneration) return;
            const old = payload.old as any;
            set((s) => ({
              followers: s.followers.filter((f) =>
                old.id ? f.id !== old.id
                  : !(f.entityType === old.entity_type && f.entityId === old.entity_id && f.userId === old.user_id)
              ),
            }));
          }
        )
        .subscribe((status, err) => {
          if (activeGeneration !== _followersSubscriptionGeneration) return;
          if (status === 'CHANNEL_ERROR') {
            console.error('[followers] realtime channel error — will retry', err);
            supabase.removeChannel(_followersChannel!);
            _followersChannel = null;
            setTimeout(setupFollowers, 3000);
          } else if (status === 'TIMED_OUT') {
            console.warn('[followers] realtime channel timed out — will retry');
            supabase.removeChannel(_followersChannel!);
            _followersChannel = null;
            setTimeout(setupFollowers, 3000);
          }
        });
    };

    setupFollowers();
  },

  unsubscribeFromFollowers: () => {
    _followersSubscriptionGeneration += 1;
    if (_followersChannel) {
      supabase.removeChannel(_followersChannel);
      _followersChannel = null;
    }
  },

  // Legacy combined subscription — kept for backward compat
  subscribeToNotifications: async (workspaceId) => {
    await get().subscribeToNotificationsGlobal();
    await get().subscribeToFollowers(workspaceId);
  },

  unsubscribeFromNotifications: () => {
    get().unsubscribeFromNotificationsGlobal();
    get().unsubscribeFromFollowers();
  },

  subscribeToAllSessions: async (sessions) => {
    const subscriptionGeneration = ++_sessionSubscriptionGeneration;
    // Tear down existing per-session channels
    for (const { client, channel } of _sessionSubs.values()) {
      try { client.removeChannel(channel); } catch { /* ignore */ }
    }
    _sessionSubs.clear();

    for (const sess of sessions.filter((s) => !s.stale)) {
      const client = getSessionClient(sess);
      const channel = client
        .channel(`notif-${sess.user_id}-${Date.now()}`)
        .on(
          'postgres_changes',
          { event: 'INSERT', schema: 'public', table: 'notifications', filter: `recipient_user_id=eq.${sess.user_id}` },
          (payload: any) => {
            if (subscriptionGeneration !== _sessionSubscriptionGeneration) return;
            const n = rowToNotification(payload.new);
            set((s) => {
              if (s.notifications.some((x) => x.id === n.id)) return s;
              return { notifications: [n, ...s.notifications] };
            });
          },
        )
        .subscribe();
      _sessionSubs.set(sess.user_id, { client, channel });
    }
  },

  unsubscribeFromAllSessions: () => {
    _sessionSubscriptionGeneration += 1;
    for (const { client, channel } of _sessionSubs.values()) {
      try { client.removeChannel(channel); } catch { /* ignore */ }
    }
    _sessionSubs.clear();
  },

  fetchNotificationsForAllSessions: async (sessions) => {
    const requestGeneration = ++_notificationsRequestGeneration;
    const live = sessions.filter((session) => !session.stale);
    const requestedUsers = new Set(live.map((session) => session.user_id));
    set((state) => ({
      loading: live.length > 0,
      notifications: state.notifications.filter((notification) =>
        requestedUsers.has(notification.recipientUserId)),
    }));
    if (!live.length) return;
    const allNotifs: AppNotification[] = [];
    await Promise.all(
      live.map(async (sess) => {
        try {
          const client = getSessionClient(sess);
          const { data } = await (client as any)
            .from('notifications')
            .select('*')
            .eq('recipient_user_id', sess.user_id)
            .order('created_at', { ascending: false })
            .limit(200);
          if (data) allNotifs.push(...(data as any[]).map(rowToNotification));
        } catch { /* ignore per-session errors */ }
      }),
    );
    if (requestGeneration !== _notificationsRequestGeneration) return;
    const seen = new Set<string>();
    const deduped = allNotifs
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .filter((n) => (seen.has(n.id) ? false : (seen.add(n.id), true)));
    set({ notifications: deduped, loading: false });
  },
}));
